import { useEffect, useRef, useMemo, useState, useCallback, type ReactNode } from 'react';
import {
  startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isToday,
  format, differenceInMinutes,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { CalendarEvent } from './types';

const PX_PER_HOUR = 64;
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const TOTAL_HEIGHT = 24 * PX_PER_HOUR;
const ESTADOS_REAGENDABLES = ['programada', 'confirmada'];
const DRAG_THRESHOLD_PX = 4;

function dayKey(d: Date) {
  return format(d, 'yyyy-MM-dd');
}

const ES_DAYS_CAL = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];

function tMins(t: string) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function getClosedRanges(clinicaHorarios: any[], day: Date): Array<{ top: number; height: number }> {
  const dayES = ES_DAYS_CAL[day.getDay()];
  const dayH = clinicaHorarios.filter(h => h.diaSemana === dayES);
  // No entries for this day but clinic has a schedule → day is closed
  if (!dayH.length) return [{ top: 0, height: TOTAL_HEIGHT }];

  const open = dayH.filter(h => !h.esBloqueo).sort((a, b) => tMins(a.horaInicio) - tMins(b.horaInicio));
  const blocked = dayH.filter(h => h.esBloqueo);

  if (!open.length) return [{ top: 0, height: TOTAL_HEIGHT }];

  const closed: Array<{ start: number; end: number }> = [];
  const first = tMins(open[0].horaInicio);
  if (first > 0) closed.push({ start: 0, end: first });
  for (let i = 0; i < open.length - 1; i++) {
    const gap = { start: tMins(open[i].horaFin), end: tMins(open[i + 1].horaInicio) };
    if (gap.start < gap.end) closed.push(gap);
  }
  const last = tMins(open[open.length - 1].horaFin);
  if (last < 24 * 60) closed.push({ start: last, end: 24 * 60 });
  for (const b of blocked) closed.push({ start: tMins(b.horaInicio), end: tMins(b.horaFin) });

  return closed.map(({ start, end }) => ({
    top: (start / 60) * PX_PER_HOUR,
    height: ((end - start) / 60) * PX_PER_HOUR,
  }));
}

function isInRanges(y: number, ranges?: Array<{ top: number; height: number }>): boolean {
  return !!ranges?.some(({ top, height }) => y >= top && y < top + height);
}

function slotFromY(y: number) {
  const clamped = Math.max(0, Math.min(y, TOTAL_HEIGHT - 1));
  const totalMins = (clamped / PX_PER_HOUR) * 60;
  const hours = Math.min(Math.floor(totalMins / 60), 23);
  const mins  = Math.floor((totalMins % 60) / 30) * 30;
  return { hours, mins };
}

interface LayoutEvent {
  event: CalendarEvent;
  col: number;
  totalCols: number;
  top: number;
  height: number;
}

function layoutDayEvents(dayEvents: CalendarEvent[]): LayoutEvent[] {
  if (!dayEvents.length) return [];

  const sorted = [...dayEvents].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
  );

  const colEnds: Date[] = [];
  const placed: Array<{ event: CalendarEvent; col: number }> = [];

  for (const ev of sorted) {
    const start = new Date(ev.start);
    const end = new Date(ev.end);
    let col = 0;
    while (col < colEnds.length && colEnds[col] > start) col++;
    if (col >= colEnds.length) colEnds.push(end);
    else colEnds[col] = end;
    placed.push({ event: ev, col });
  }

  const totalCols = colEnds.length || 1;
  return placed.map(({ event, col }) => {
    const start = new Date(event.start);
    const end = new Date(event.end);
    const midnight = new Date(start);
    midnight.setHours(0, 0, 0, 0);
    const minsFromMidnight = differenceInMinutes(start, midnight);
    const duration = Math.max(differenceInMinutes(end, start), 15);
    return {
      event, col, totalCols,
      top: (minsFromMidnight / 60) * PX_PER_HOUR,
      height: (duration / 60) * PX_PER_HOUR,
    };
  });
}

interface WeekDayViewProps {
  date: Date;
  events: CalendarEvent[];
  mode: 'week' | 'day';
  onDateChange: (d: Date) => void;
  onEventClick?: (e: CalendarEvent) => void;
  onEventCreate?: (partial: { start: string; end: string }) => void;
  onEventDrop?: (change: { id: string; start: string; end: string }) => void;
  readOnly?: boolean;
  clinicaHorarios?: any[];
  /** Franja opcional bajo los encabezados con contenido "todo el día" por día (p. ej. tareas). */
  bandaTodoElDia?: (day: Date) => ReactNode;
}

interface DropPreview {
  dayKey: string;
  top: number;
  height: number;
  label: string;
  blocked: boolean;
}

/* ── DayColumn with hover slot ──────────────────────────────────────────── */

interface DayColumnProps {
  day: Date;
  events: CalendarEvent[];
  closedRanges?: Array<{ top: number; height: number }>;
  readOnly?: boolean;
  onEventClick?: (e: CalendarEvent) => void;
  onEventCreate?: (partial: { start: string; end: string }) => void;
  onEventMouseDown?: (e: React.MouseEvent, event: CalendarEvent) => void;
  draggingId?: string | null;
  dropPreview?: DropPreview | null;
  columnRef?: (el: HTMLDivElement | null) => void;
}

function DayColumn({
  day, events, closedRanges, readOnly, onEventClick, onEventCreate,
  onEventMouseDown, draggingId, dropPreview, columnRef,
}: DayColumnProps) {
  const [hoverSlot, setHoverSlot] = useState<{ top: number; label: string } | null>(null);
  const [hoverBlocked, setHoverBlocked] = useState(false);
  const laid = layoutDayEvents(events);
  const isDropTarget = dropPreview?.dayKey === dayKey(day);

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (readOnly || draggingId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    if (isInRanges(y, closedRanges)) {
      setHoverSlot(null);
      setHoverBlocked(true);
      return;
    }
    setHoverBlocked(false);
    const { hours, mins } = slotFromY(y);
    const top = (hours + mins / 60) * PX_PER_HOUR;
    const label = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    setHoverSlot({ top, label });
  }

  function handleMouseLeave() {
    setHoverSlot(null);
    setHoverBlocked(false);
  }

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    if (readOnly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    if (isInRanges(y, closedRanges)) return;
    const { hours, mins } = slotFromY(y);
    const start = new Date(day);
    start.setHours(hours, mins, 0, 0);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + 30);
    onEventCreate?.({ start: start.toISOString(), end: end.toISOString() });
  }

  return (
    <div
      ref={columnRef}
      className={cn(
        'flex-1 relative border-r border-gray-100 last:border-r-0',
        !readOnly && (hoverBlocked ? 'cursor-not-allowed' : 'cursor-cell'),
      )}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Hour lines */}
      {HOURS.map(h => (
        <div
          key={h}
          className="absolute inset-x-0 border-t border-gray-100 pointer-events-none"
          style={{ top: h * PX_PER_HOUR }}
        >
          <div
            className="absolute inset-x-0 border-t border-gray-50 pointer-events-none"
            style={{ top: PX_PER_HOUR / 2 }}
          />
        </div>
      ))}

      {/* Closed-hour overlays */}
      {closedRanges?.map(({ top, height }, i) => (
        <div
          key={`closed-${i}`}
          className="absolute inset-x-0 pointer-events-none z-[1]"
          style={{
            top,
            height,
            background: 'repeating-linear-gradient(45deg,transparent,transparent 5px,rgba(0,0,0,0.018) 5px,rgba(0,0,0,0.018) 10px)',
            backgroundColor: 'rgba(0,0,0,0.028)',
          }}
        />
      ))}

      {isToday(day) && (
        <div className="absolute inset-0 bg-primary-50/20 pointer-events-none" />
      )}

      {isToday(day) && <NowLine />}

      {/* Hover slot highlight */}
      {!readOnly && !draggingId && hoverSlot !== null && (
        <div
          className="absolute inset-x-0 pointer-events-none z-5"
          style={{ top: hoverSlot.top, height: PX_PER_HOUR / 2 }}
        >
          <div className="absolute inset-0 bg-primary-400/10 border-t-2 border-primary-400/50" />
          <span className="absolute left-1.5 top-0.5 text-[10px] font-semibold text-primary-600 leading-none select-none">
            {hoverSlot.label}
          </span>
        </div>
      )}

      {/* Drop preview while dragging an event */}
      {isDropTarget && dropPreview && (
        <div
          className="absolute inset-x-0.5 pointer-events-none z-20 rounded-md"
          style={{
            top: dropPreview.top,
            height: Math.max(dropPreview.height, 22),
            backgroundColor: dropPreview.blocked ? 'rgba(239,68,68,0.12)' : 'rgba(14,133,191,0.14)',
            border: `2px dashed ${dropPreview.blocked ? '#ef4444' : '#0e85bf'}`,
          }}
        >
          <span className={cn(
            'absolute left-1.5 top-0.5 text-[10px] font-semibold leading-none select-none',
            dropPreview.blocked ? 'text-red-600' : 'text-primary-600',
          )}>
            {dropPreview.label}
          </span>
        </div>
      )}

      {/* Events */}
      {laid.map(({ event, col, totalCols, top, height }) => {
        const colWidth = 100 / totalCols;
        const cita = (event as any)._cita;
        const draggable = !readOnly && !!onEventMouseDown && ESTADOS_REAGENDABLES.includes(cita?.estado);
        const isDragging = draggingId === event.id;
        return (
          <button
            key={event.id}
            onMouseDown={(e) => {
              if (!draggable) return;
              if (e.button !== 0) return;
              onEventMouseDown?.(e, event);
            }}
            onClick={e => { e.stopPropagation(); onEventClick?.(event); }}
            title={event.title}
            className={cn(
              'absolute rounded-md text-white text-[11px] font-medium px-2 py-1 overflow-hidden text-left leading-tight hover:brightness-90 transition-all z-10 border border-white/20',
              draggable && 'cursor-grab active:cursor-grabbing',
              isDragging && 'opacity-30',
            )}
            style={{
              top: top + 1,
              height: Math.max(height - 2, 32),
              left: `${colWidth * col + 0.5}%`,
              width: `${colWidth - 1}%`,
              backgroundColor: event.color ?? '#0c77a6',
            }}
          >
            <span className="truncate block pt-0.5">
              {cita?.esRecurrente && (
                <span className="mr-0.5 opacity-70">↻</span>
              )}
              {event.title}
            </span>
            {height > 28 && (
              <span className="block text-white/80 text-[10px]">
                {format(new Date(event.start), 'HH:mm')}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ── WeekDayView ────────────────────────────────────────────────────────── */

export function WeekDayView({
  date, events, mode, onDateChange, onEventClick, onEventCreate, onEventDrop, readOnly, clinicaHorarios = [], bandaTodoElDia,
}: WeekDayViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const dragRef = useRef<{
    id: string;
    offsetY: number;
    durationMin: number;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);
  const justDraggedRef = useRef(false);
  const dropResultRef = useRef<{ id: string; start: string; end: string } | null>(null);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropPreview, setDropPreview] = useState<DropPreview | null>(null);

  const days = useMemo(() => {
    if (mode === 'day') return [date];
    const start = startOfWeek(date, { weekStartsOn: 1 });
    const end = endOfWeek(date, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [date, mode]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 7 * PX_PER_HOUR - 40;
    }
  }, [mode]);

  const handleEventMouseDown = useCallback((e: React.MouseEvent, event: CalendarEvent) => {
    if (!onEventDrop) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const durationMin = Math.max(differenceInMinutes(new Date(event.end), new Date(event.start)), 15);
    dragRef.current = {
      id: event.id,
      offsetY: e.clientY - rect.top,
      durationMin,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
  }, [onEventDrop]);

  const handleEventClick = useCallback((event: CalendarEvent) => {
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return;
    }
    onEventClick?.(event);
  }, [onEventClick]);

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      const drag = dragRef.current;
      if (!drag) return;

      if (!drag.moved) {
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        drag.moved = true;
        setDraggingId(drag.id);
      }

      let target: { key: string; rect: DOMRect } | null = null;
      for (const [key, el] of columnRefs.current) {
        const rect = el.getBoundingClientRect();
        if (e.clientX >= rect.left && e.clientX < rect.right) {
          target = { key, rect };
          break;
        }
      }
      if (!target) {
        setDropPreview(null);
        dropResultRef.current = null;
        return;
      }

      const day = days.find(d => dayKey(d) === target!.key);
      if (!day) return;

      const y = (e.clientY - target.rect.top) - drag.offsetY;
      const clampedY = Math.max(0, Math.min(y, TOTAL_HEIGHT - 1));
      const closed = clinicaHorarios.length ? getClosedRanges(clinicaHorarios, day) : undefined;
      const blocked = isInRanges(clampedY, closed);
      const { hours, mins } = slotFromY(clampedY);
      const top = (hours + mins / 60) * PX_PER_HOUR;
      const height = (drag.durationMin / 60) * PX_PER_HOUR;
      const label = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
      setDropPreview({ dayKey: target.key, top, height, label, blocked });

      const start = new Date(day);
      start.setHours(hours, mins, 0, 0);
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + drag.durationMin);
      dropResultRef.current = blocked ? null : { id: drag.id, start: start.toISOString(), end: end.toISOString() };
    }

    function handleMouseUp() {
      const drag = dragRef.current;
      if (drag?.moved) {
        justDraggedRef.current = true;
        if (dropResultRef.current) onEventDrop?.(dropResultRef.current);
      }
      dragRef.current = null;
      dropResultRef.current = null;
      setDraggingId(null);
      setDropPreview(null);
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [days, clinicaHorarios, onEventDrop]);

  return (
    <div className="flex flex-col h-full">
      {/* Day headers */}
      <div className="flex flex-shrink-0 border-b border-gray-100 bg-white">
        <div className="w-14 flex-shrink-0 border-r border-gray-100" />
        {days.map(day => (
          <div
            key={day.toISOString()}
            onClick={() => onDateChange(day)}
            className="flex-1 py-2 text-center border-r border-gray-100 last:border-r-0 cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <p className="text-[11px] font-medium text-text-secondary uppercase leading-none">
              {format(day, 'EEE', { locale: es })}
            </p>
            <p className={cn(
              'text-sm font-bold mt-1 w-7 h-7 flex items-center justify-center rounded-full mx-auto',
              isToday(day) ? 'bg-primary-500 text-white' : 'text-text-primary',
            )}>
              {format(day, 'd')}
            </p>
          </div>
        ))}
      </div>

      {bandaTodoElDia && (
        <div className="flex flex-shrink-0 border-b border-gray-100 bg-gray-50/50">
          <div className="w-14 flex-shrink-0 border-r border-gray-100 pr-1.5 pt-1.5 text-right">
            <span className="text-[9px] font-medium uppercase tracking-wide text-text-secondary">Tareas</span>
          </div>
          {days.map(day => (
            <div key={day.toISOString()} className="flex-1 min-w-0 min-h-[1.75rem] max-h-28 overflow-y-auto p-1 border-r border-gray-100 last:border-r-0">
              {bandaTodoElDia(day)}
            </div>
          ))}
        </div>
      )}

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="flex" style={{ height: TOTAL_HEIGHT }}>
          {/* Hour labels */}
          <div className="w-14 flex-shrink-0 relative select-none border-r border-gray-100">
            {HOURS.map(h => (
              <div
                key={h}
                className="absolute right-2 text-[10px] text-text-secondary leading-none"
                style={{ top: h * PX_PER_HOUR - 7 }}
              >
                {h !== 0 && `${String(h).padStart(2, '0')}:00`}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map(day => {
            const key = dayKey(day);
            return (
              <DayColumn
                key={day.toISOString()}
                day={day}
                events={events.filter(e => isSameDay(new Date(e.start), day))}
                closedRanges={clinicaHorarios.length ? getClosedRanges(clinicaHorarios, day) : undefined}
                readOnly={readOnly}
                onEventClick={handleEventClick}
                onEventCreate={onEventCreate}
                onEventMouseDown={handleEventMouseDown}
                draggingId={draggingId}
                dropPreview={dropPreview}
                columnRef={(el) => {
                  if (el) columnRefs.current.set(key, el);
                  else columnRefs.current.delete(key);
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function NowLine() {
  const now = new Date();
  const top = (now.getHours() + now.getMinutes() / 60) * PX_PER_HOUR;
  return (
    <div
      className="absolute inset-x-0 pointer-events-none z-20 flex items-center"
      style={{ top }}
    >
      <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 -ml-1" />
      <div className="flex-1 border-t border-red-500" />
    </div>
  );
}
